import { useId, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  Check,
  ChevronDown,
  Copy,
  Loader2,
  Mail,
  MessageSquare,
  Send,
  UserCheck,
  UserPlus,
  X,
} from "lucide-react";
import { Button } from "@nexus/ui";
import { cn } from "@nexus/ui";
import {
  MAX_INVITE_EMAILS,
  type AccessGroupItem,
  type BulkInviteResultItem,
  type UserItem,
} from "@/features/identity-hub/api/identity-hub.api";
import { useCreateInvitesBulk } from "@/features/identity-hub/hooks/useIdentityHub";
import { isPerTenantAssignable } from "@/features/identity-hub/lib/access-scope";
import {
  parseTemplateRoleOptionId,
  unprojectedWildcardOptions,
  type InviteWildcardTemplate,
} from "@/features/identity-hub/lib/invite-role-options";
import { buildInviteUrl } from "@/features/auth/lib/invite";
import { UserPhotoMark } from "./UserPhotoMark";

const DEFAULT_TTL_HOURS = 336; // 14 days — matches InvitePersonDialog default
const HOURS_PER_DAY = 24;
const INVITE_TTL_DAYS = DEFAULT_TTL_HOURS / HOURS_PER_DAY;
const MAX_INVITE_NOTE_LENGTH = 1000; // mirrors the `message` max in services/api invite.schemas.ts
const MAX_ACCOUNT_SUGGESTIONS = 5;
const MAX_MEMBER_SUGGESTIONS = 2;
const COPIED_FEEDBACK_MS = 2000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ENTRY_SEPARATOR_RE = /[\s,;]+/;
const COMMIT_KEYS = new Set([",", ";"]);

const isValidEmail = (s: string) => EMAIL_RE.test(s);

type PeopleChip =
  | { kind: "account"; user: UserItem }
  | { kind: "invite"; email: string }
  | { kind: "invalid"; raw: string };

type SuggestionOption =
  | { kind: "account"; user: UserItem }
  | { kind: "invite"; email: string };

function chipKey(chip: PeopleChip): string {
  if (chip.kind === "account") return `account:${chip.user.id}`;
  if (chip.kind === "invite") return `invite:${chip.email}`;
  return `invalid:${chip.raw}`;
}

function matchesQuery(user: UserItem, q: string): boolean {
  return user.displayName.toLowerCase().includes(q) || user.email.toLowerCase().includes(q);
}

export interface OrgMembersInviteByEmailPanelProps {
  orgId?: string;
  orgName: string;
  assignableRoles: AccessGroupItem[];
  /** Active wildcard catalog roles with no live group on this org. Superadmin only. */
  wildcardTemplates?: InviteWildcardTemplate[];
  /** People with an account who are not members of this org yet. */
  candidates: UserItem[];
  /** Current members — shown as "already in" when they match the search. */
  members: UserItem[];
  canInviteByEmail: boolean;
  onAddMembers: (userIds: string[], accessId: string) => void;
  /** Resolves with the live access id when the grant projected the catalog role. */
  onGrantTemplate?: (userIds: string[], templateId: string) => Promise<string | null | void> | string | null | void;
  onClose: () => void;
  onComplete?: () => void;
}

export function OrgMembersInviteByEmailPanel({
  orgId,
  orgName,
  assignableRoles,
  wildcardTemplates = [],
  candidates,
  members,
  canInviteByEmail,
  onAddMembers,
  onGrantTemplate,
  onClose,
  onComplete,
}: OrgMembersInviteByEmailPanelProps) {
  const { t } = useTranslation();
  const createInvites = useCreateInvitesBulk();
  const listboxId = useId();
  const roleSelectId = useId();
  const noteInputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const [chips, setChips] = useState<PeopleChip[]>([]);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const roleChoices = useMemo(() => {
    // FIXED projections are granted from Assign Role. Sending their live id
    // through /invites/bulk is skipped, so they stay out of this picker.
    const live = assignableRoles
      .filter((role) => isPerTenantAssignable(role))
      .map((role) => ({ id: role.id, name: role.name }));
    return [...live, ...unprojectedWildcardOptions(assignableRoles, wildcardTemplates)];
  }, [assignableRoles, wildcardTemplates]);
  const [selectedAccessId, setSelectedAccessId] = useState<string | null>(
    roleChoices.length === 1 ? roleChoices[0].id : null,
  );
  const [note, setNote] = useState("");
  const [results, setResults] = useState<BulkInviteResultItem[] | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const accountIds = chips.flatMap((c) => (c.kind === "account" ? [c.user.id] : []));
  const inviteEmails = chips.flatMap((c) => (c.kind === "invite" ? [c.email] : []));
  const invalidCount = chips.filter((c) => c.kind === "invalid").length;

  const candidatesByEmail = useMemo(
    () => new Map(candidates.map((u) => [u.email.toLowerCase(), u])),
    [candidates],
  );
  const memberEmails = useMemo(() => new Set(members.map((u) => u.email.toLowerCase())), [members]);

  const q = query.trim().toLowerCase();
  const accountSuggestions = q
    ? candidates
        .filter((u) => !accountIds.includes(u.id) && matchesQuery(u, q))
        .slice(0, MAX_ACCOUNT_SUGGESTIONS)
    : [];
  const memberSuggestions = q
    ? members.filter((u) => matchesQuery(u, q)).slice(0, MAX_MEMBER_SUGGESTIONS)
    : [];
  const isKnownEmail = candidatesByEmail.has(q) || memberEmails.has(q);
  const showInviteRow = canInviteByEmail && !!q && !isKnownEmail && !inviteEmails.includes(q);
  const isInviteRowValid = showInviteRow && isValidEmail(q) && inviteEmails.length < MAX_INVITE_EMAILS;

  const options: SuggestionOption[] = [
    ...accountSuggestions.map((user) => ({ kind: "account" as const, user })),
    ...(isInviteRowValid ? [{ kind: "invite" as const, email: q }] : []),
  ];
  const activeIndex = options.length === 0 ? -1 : Math.min(highlightIndex, options.length - 1);
  const isListOpen = !!q && (options.length > 0 || memberSuggestions.length > 0 || showInviteRow);

  const addChips = (next: PeopleChip[]) => {
    if (next.length === 0) return;
    setChips((prev) => {
      const seen = new Set(prev.map(chipKey));
      const merged = [...prev];
      let inviteCount = prev.filter((c) => c.kind === "invite").length;
      for (const chip of next) {
        const key = chipKey(chip);
        if (seen.has(key)) continue;
        if (chip.kind === "invite") {
          if (inviteCount >= MAX_INVITE_EMAILS) continue;
          inviteCount += 1;
        }
        seen.add(key);
        merged.push(chip);
      }
      return merged;
    });
  };

  const resetQuery = () => {
    setQuery("");
    setHighlightIndex(0);
  };

  const toChip = (part: string): PeopleChip | null => {
    const email = part.toLowerCase();
    const account = candidatesByEmail.get(email);
    if (account) return { kind: "account", user: account };
    if (memberEmails.has(email)) return null;
    if (!canInviteByEmail) return null;
    return isValidEmail(email) ? { kind: "invite", email } : { kind: "invalid", raw: part };
  };

  const commitText = (raw: string) => {
    const parts = raw.split(ENTRY_SEPARATOR_RE).map((s) => s.trim()).filter(Boolean);
    addChips(parts.flatMap((part) => toChip(part) ?? []));
    resetQuery();
  };

  const pickOption = (option: SuggestionOption) => {
    addChips([option.kind === "account" ? { kind: "account", user: option.user } : { kind: "invite", email: option.email }]);
    resetQuery();
    inputRef.current?.focus();
  };

  const removeChip = (key: string) => setChips((prev) => prev.filter((c) => chipKey(c) !== key));

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && options.length > 0) {
      e.preventDefault();
      setHighlightIndex((activeIndex + 1) % options.length);
    } else if (e.key === "ArrowUp" && options.length > 0) {
      e.preventDefault();
      setHighlightIndex((activeIndex - 1 + options.length) % options.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) pickOption(options[activeIndex]);
      else if (q) commitText(query);
    } else if (COMMIT_KEYS.has(e.key)) {
      e.preventDefault();
      commitText(query);
    } else if (e.key === "Backspace" && query === "" && chips.length > 0) {
      removeChip(chipKey(chips[chips.length - 1]));
    } else if (e.key === "Escape") {
      e.preventDefault();
      if (query) resetQuery();
      else onClose();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (!ENTRY_SEPARATOR_RE.test(text.trim())) return;
    e.preventDefault();
    commitText(text);
  };

  const hasEntries = accountIds.length > 0 || inviteEmails.length > 0;
  const selectedTemplateId = selectedAccessId ? parseTemplateRoleOptionId(selectedAccessId) : null;
  const canSubmit =
    hasEntries &&
    invalidCount === 0 &&
    !!selectedAccessId &&
    (!selectedTemplateId || !!orgId) &&
    !createInvites.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedAccessId) return;
    // Grant existing accounts first. The invite then reuses that live id so
    // both sides do not create the same (org, role name) at once.
    let reusedAccessId: string | null = null;
    if (accountIds.length > 0) {
      if (selectedTemplateId) {
        try {
          const grantedId = await onGrantTemplate?.(accountIds, selectedTemplateId);
          if (typeof grantedId === "string" && grantedId.length > 0) reusedAccessId = grantedId;
        } catch {
          // The grant hook toasts. Emails still invite through templateGrants.
        }
      } else {
        onAddMembers(accountIds, selectedAccessId);
      }
    }
    if (inviteEmails.length === 0) {
      onComplete?.();
      return;
    }
    const message = note.trim();
    const inviteAccessId = reusedAccessId ?? (selectedTemplateId ? null : selectedAccessId);
    try {
      const res = await createInvites.mutateAsync({
        emails: inviteEmails,
        accessIds: inviteAccessId ? [inviteAccessId] : [],
        ...(selectedTemplateId && !reusedAccessId && orgId
          ? { templateGrants: [{ templateId: selectedTemplateId, tenantId: orgId }] }
          : {}),
        ttlHours: DEFAULT_TTL_HOURS,
        sendEmail: true,
        ...(message ? { message } : {}),
      });
      if (res.every((r) => r.success)) {
        onComplete?.();
        return;
      }
      setChips((prev) => prev.filter((c) => c.kind !== "account"));
      setResults(res);
    } catch {
      // Hook shows error toast
    }
  };

  const copyLink = (invite: { id?: string; token?: string }) => {
    if (!invite.id || !invite.token) return;
    const id = invite.id;
    void navigator.clipboard.writeText(buildInviteUrl(invite.token)).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), COPIED_FEEDBACK_MS);
    }).catch(() => undefined);
  };

  const submitLabel =
    accountIds.length > 0 && inviteEmails.length > 0
      ? t("iam.orgDrawer.addPeople.submitAddAndInvite", "Add {{added}} · invite {{invited}}", {
          added: accountIds.length,
          invited: inviteEmails.length,
        })
      : accountIds.length > 0
        ? t("iam.orgDrawer.addPeople.submitAdd", "Add {{count}}", { count: accountIds.length })
        : inviteEmails.length > 0
          ? t("iam.orgDrawer.addPeople.submitInvite", "Invite {{count}}", { count: inviteEmails.length })
          : t("iam.orgDrawer.addPeople.submitEmpty", "Add people");

  if (results) {
    const succeeded = results.filter((r) => r.success);
    const failed = results.filter((r) => !r.success);
    return (
      <div
        className="animate-in slide-in-from-bottom-2 fade-in flex shrink-0 flex-col border-t border-border bg-background duration-200"
        data-testid="org-invite-by-email-results"
      >
        <div className="flex flex-col gap-3 px-6 py-5">
          <p className="text-[13px] font-semibold text-foreground">
            {t("iam.orgDrawer.inviteByEmail.resultsTitle", "Invite results")}
          </p>
          <ul className="flex max-h-40 flex-col gap-1 overflow-y-auto">
            {succeeded.map((r) => (
              <li
                key={r.email}
                className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-[11px]"
              >
                <span className="truncate font-medium">{r.email}</span>
                {r.token ? (
                  <button
                    type="button"
                    className="inline-flex shrink-0 items-center gap-1 text-primary hover:underline"
                    onClick={() => copyLink(r)}
                  >
                    {copiedId === r.id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copiedId === r.id
                      ? t("iam.inviteDialog.copied", "Copied")
                      : t("iam.inviteDialog.copyLink", "Copy link")}
                  </button>
                ) : (
                  <span className="shrink-0 text-muted-foreground">
                    {t("iam.orgDrawer.inviteByEmail.sent", "Sent")}
                  </span>
                )}
              </li>
            ))}
            {failed.map((r) => (
              <li
                key={`fail-${r.email}`}
                className="rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-[11px] text-destructive"
              >
                {r.email}: {r.error ?? t("iam.inviteDialog.errorUnknown")}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-end border-t border-border px-5 py-3">
          <Button size="sm" onClick={onClose}>
            {t("iam.inviteDialog.done", "Done")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="animate-in slide-in-from-bottom-2 fade-in flex max-h-[65%] shrink-0 flex-col border-t border-border bg-background shadow-[0_-12px_24px_-16px_rgb(0_0_0/0.18)] duration-200"
      data-testid="org-invite-by-email-panel"
    >
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-6 py-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-[13px] font-semibold text-foreground">
              {t("iam.orgDrawer.addPeople.title", "Add people to {{org}}", { org: orgName })}
            </p>
            <p className="text-xs text-muted-foreground">
              {canInviteByEmail
                ? t(
                    "iam.orgDrawer.addPeople.subtitle",
                    "Search people who already have an account, or type any email to send an invite.",
                  )
                : t("iam.orgDrawer.addPeople.subtitleNoInvite", "Search people who already have an account.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={t("iam.orgDrawer.addPeople.closeAria", "Close add people")}
          >
            <X className="size-3.5" />
          </button>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-foreground">
            {t("iam.orgDrawer.addPeople.peopleLabel", "People")}
          </span>
          <div
            className="flex min-h-10 w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border border-input-border bg-background px-2 py-1.5 transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15"
            onClick={() => inputRef.current?.focus()}
          >
            {chips.map((chip) => (
              <PersonChip key={chipKey(chip)} chip={chip} onRemove={() => removeChip(chipKey(chip))} />
            ))}
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={isListOpen}
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
              aria-label={t("iam.orgDrawer.addPeople.peopleLabel", "People")}
              aria-invalid={invalidCount > 0 || undefined}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlightIndex(0);
              }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => {
                if (isValidEmail(q)) commitText(query);
              }}
              placeholder={
                chips.length === 0
                  ? canInviteByEmail
                    ? t("iam.orgDrawer.addPeople.placeholder", "Name, email, or paste a list of emails…")
                    : t("iam.orgDrawer.addPeople.placeholderNoInvite", "Search by name or email…")
                  : ""
              }
              className="h-7 min-w-[120px] flex-1 bg-transparent px-1 text-[13px] outline-none placeholder:text-muted-foreground/70"
              data-testid="org-invite-emails-input"
            />
          </div>

          {isListOpen && (
            <ul
              id={listboxId}
              role="listbox"
              aria-label={t("iam.orgDrawer.addPeople.suggestionsAria", "Suggestions")}
              className="flex flex-col overflow-hidden rounded-md border border-border bg-background shadow-sm"
            >
              {accountSuggestions.map((user, index) => (
                <li
                  key={user.id}
                  id={`${listboxId}-${index}`}
                  role="option"
                  aria-selected={activeIndex === index}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlightIndex(index)}
                  onClick={() => pickOption({ kind: "account", user })}
                  className={cn(
                    "flex cursor-pointer items-center gap-2.5 border-b border-border-subtle px-3 py-2 last:border-b-0",
                    activeIndex === index && "bg-muted",
                  )}
                >
                  <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="sm" className="shrink-0" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[13px] font-medium text-foreground">{user.displayName}</span>
                    <span className="truncate text-[11px] text-muted-foreground">{user.email}</span>
                  </div>
                </li>
              ))}
              {memberSuggestions.map((user) => (
                <li
                  key={`member-${user.id}`}
                  role="option"
                  aria-selected={false}
                  aria-disabled
                  className="flex items-center gap-2.5 border-b border-border-subtle bg-muted/50 px-3 py-2 last:border-b-0"
                >
                  <UserPhotoMark name={user.displayName} photoUrl={user.photoUrl} size="sm" className="shrink-0 opacity-70" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[13px] font-medium text-foreground/80">{user.displayName}</span>
                    <span className="truncate text-[11px] text-muted-foreground">{user.email}</span>
                  </div>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {t("iam.orgDrawer.addPeople.alreadyMember", "Already in {{org}}", { org: orgName })}
                  </span>
                </li>
              ))}
              {showInviteRow && (
                <li
                  id={isInviteRowValid ? `${listboxId}-${accountSuggestions.length}` : undefined}
                  role="option"
                  aria-selected={isInviteRowValid && activeIndex === accountSuggestions.length}
                  aria-disabled={!isInviteRowValid || undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => isInviteRowValid && setHighlightIndex(accountSuggestions.length)}
                  onClick={() => isInviteRowValid && pickOption({ kind: "invite", email: q })}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2 text-[13px]",
                    isInviteRowValid ? "cursor-pointer text-foreground" : "text-muted-foreground",
                    isInviteRowValid && activeIndex === accountSuggestions.length && "bg-muted",
                  )}
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-subtle text-primary">
                    <Mail className="size-3.5" aria-hidden />
                  </span>
                  {isInviteRowValid
                    ? t("iam.orgDrawer.addPeople.inviteOption", "Invite “{{email}}”", { email: q })
                    : inviteEmails.length >= MAX_INVITE_EMAILS
                      ? t("iam.orgDrawer.addPeople.inviteCapReached", "You can invite up to {{max}} people at a time", {
                          max: MAX_INVITE_EMAILS,
                        })
                      : t("iam.orgDrawer.addPeople.inviteIncomplete", "Invite “{{email}}” — finish typing an email address", {
                          email: query.trim(),
                        })}
                </li>
              )}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={roleSelectId} className="text-xs font-medium text-foreground">
              {t("iam.orgDrawer.addPeople.roleLabel", "Role for everyone added")}
            </label>
            {roleChoices.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("iam.orgDrawer.addMember.noGrantableRoles", "No roles you can grant")}
              </p>
            ) : (
              <div className="relative">
                <select
                  id={roleSelectId}
                  value={selectedAccessId ?? ""}
                  onChange={(e) => setSelectedAccessId(e.target.value || null)}
                  className="h-10 w-full appearance-none rounded-md border border-input-border bg-background pl-3 pr-9 text-[13px] text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                  data-testid="org-add-people-role"
                >
                  {!selectedAccessId && (
                    <option value="" disabled>
                      {t("iam.orgDrawer.addPeople.rolePlaceholder", "Pick a role")}
                    </option>
                  )}
                  {roleChoices.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
              </div>
            )}
          </div>

          {canInviteByEmail && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor={noteInputId} className="text-xs font-medium text-foreground">
                {t("iam.orgDrawer.addPeople.noteLabel", "Note in the invite email")}
              </label>
              <div className="flex h-10 items-center gap-2 rounded-md border border-dashed border-input-border bg-background px-3 focus-within:border-solid focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15">
                <MessageSquare className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <input
                  id={noteInputId}
                  type="text"
                  value={note}
                  maxLength={MAX_INVITE_NOTE_LENGTH}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("iam.orgDrawer.addPeople.notePlaceholder", "Add a personal note (optional)")}
                  className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3 border-t border-border px-5 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5" aria-live="polite">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground">
            {accountIds.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <UserCheck className="size-3.5 text-success-subtle-foreground" aria-hidden />
                {t("iam.orgDrawer.addPeople.addedNow", {
                  count: accountIds.length,
                  defaultValue_one: "{{count}} added now",
                  defaultValue_other: "{{count}} added now",
                })}
              </span>
            )}
            {inviteEmails.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Send className="size-3.5 text-primary" aria-hidden />
                {t("iam.orgDrawer.addPeople.inviteEmails", {
                  count: inviteEmails.length,
                  days: INVITE_TTL_DAYS,
                  defaultValue_one: "{{count}} invite email · expires in {{days}} days",
                  defaultValue_other: "{{count}} invite emails · expires in {{days}} days",
                })}
              </span>
            )}
            {!hasEntries && invalidCount === 0 && (
              <span className="text-muted-foreground">
                {t("iam.orgDrawer.addPeople.emptyHint", "Nobody picked yet")}
              </span>
            )}
          </div>
          {invalidCount > 0 && (
            <p role="alert" className="text-[11px] text-destructive">
              {t("iam.orgDrawer.addPeople.fixInvalid", {
                count: invalidCount,
                defaultValue_one: "Fix or remove {{count}} invalid address to continue",
                defaultValue_other: "Fix or remove {{count}} invalid addresses to continue",
              })}
            </p>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t("iam.orgDrawer.addPeople.cancel", "Cancel")}
        </Button>
        <Button
          size="sm"
          className="gap-1.5"
          disabled={!canSubmit}
          aria-busy={createInvites.isPending}
          data-testid="org-invite-by-email-submit"
          onClick={() => void handleSubmit()}
        >
          {createInvites.isPending ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <UserPlus className="size-3.5" />
          )}
          {createInvites.isPending ? t("iam.orgDrawer.inviteByEmail.sending", "Sending…") : submitLabel}
        </Button>
      </div>
    </div>
  );
}

function PersonChip({ chip, onRemove }: { chip: PeopleChip; onRemove: () => void }) {
  const { t } = useTranslation();
  const label = chip.kind === "account" ? chip.user.displayName : chip.kind === "invite" ? chip.email : chip.raw;

  return (
    <span
      className={cn(
        "inline-flex h-7 max-w-full items-center gap-1.5 rounded-md border pl-1.5 pr-1 text-xs font-medium",
        chip.kind === "account" && "border-border bg-muted text-foreground",
        chip.kind === "invite" && "border-primary/30 bg-primary-subtle text-primary-subtle-foreground",
        chip.kind === "invalid" && "border-destructive/40 bg-destructive/5 text-destructive",
      )}
    >
      {chip.kind === "account" && (
        <UserPhotoMark name={chip.user.displayName} photoUrl={chip.user.photoUrl} size="sm" className="-ml-0.5 shrink-0" />
      )}
      {chip.kind === "invite" && <Mail className="size-3.5 shrink-0" aria-hidden />}
      {chip.kind === "invalid" && <AlertCircle className="size-3.5 shrink-0" aria-hidden />}
      <span className="truncate">{label}</span>
      {chip.kind === "account" && (
        <UserCheck
          className="size-3.5 shrink-0 text-success-subtle-foreground"
          aria-label={t("iam.orgDrawer.addPeople.hasAccountAria", "Has an account — added right away")}
        />
      )}
      {chip.kind === "invite" && (
        <span className="rounded-sm bg-primary/10 px-1 py-px text-[9px] font-semibold uppercase tracking-[0.4px]">
          {t("iam.orgDrawer.addPeople.inviteTag", "Invite")}
        </span>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="flex size-5 shrink-0 items-center justify-center rounded-sm transition-colors hover:bg-foreground/10"
        aria-label={t("iam.inviteDialog.removeEmailAria", { email: label })}
      >
        <X className="size-3" />
      </button>
    </span>
  );
}
